import {
  Page,
  Document,
  PDFViewer,
  Image,
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import { CustomListPDF } from "../../components/GeneratePdf/CustomListPDF";
import { Request } from "../../hooks/Request";
import dayjs from "dayjs";
import { numberWithDecimals, validateArray } from "../../utils/validate";
import CustomTablePDF from "../../components/GeneratePdf/CustomTablePDF";

// Constantes
const FIELD_NAMES = [
  "modalidadDeDespacho",
  "descripciónDeLaMercancía",
  "partidaArancelaria",
  "tributosAduaneros",
  "gastosNoFacturados",
  "gastosFacturados",
  "honorariosProfesionales",
  "pagos",
  "f2TotalNúmeroDeItems",
  "e1DatosDelProveedor",
  "asignarGestor",
  "proveedor",
  "canal(Rojo,AmarilloOVerde)",
  "designacionDeLiquidador",
  "codigoDeInterno",
  "a4AduanaDeDespacho",
  "a1NúmeroDeDeclaración",
  "a1N°DeDeclaración(Dam)",
  "a3NúmeroDeReferencia",
  "internoDeDav",
  "facturaComercial",
  "fleteI(Usd)",
  "fleteII(Usd)",
  "f6TotalValorFob",
  "f7GastosTransHastaElLugarDeImportación",
  "f8TotalCostoDelSeguro",
  "f9GastosDeCarga,Descarga,ManipuleoYOtrosGastos",
  "f10TotalValorCifAduana",
  "e16TipoDeCambio",
  "g1TributosDeterminadosGa",
  "g2TributosDeterminadosIva",
  "g4TributosDeterminadosIce",
  "instruciones(RecepcionDeDocumentos)",
  "instruciones(LiquidacionesArmadoDeCarpeta)",
  "instruciones(CanalYGestoria)",
  "instruciones(Adm_Financiera)",
  "instruciones(Archivos)",
  "documentosSeccionL",
];

const DEFAULT_TRIBUTOS = [
  { name: "Gravamen Arancelario (G.A.)", key: "ga" },
  { name: "Impto. Valor Agregado (I.V.A.)", key: "iva" },
  { name: "Impto. Consumo Especifico(I.C.E.)", key: "ice" },
  { name: "Form. SISTEMA - ANB", value: 100 },
];

const STEP_MAPPINGS = {
  1: "PRIMERA ACCION (RECEPCION DE DOCUMENTOS) C1",
  6: "SEGUNDA ACCION (LIQUIDACIONES ARMADO DE CARPETA)",
  10: "TERCERA ACCION (CANAL Y GESTORIA)",
  12: "CUARTA ACCION (ADM. FINANCIERA)",
  14: "QUINTA ACCION (LIQUIDACIONES ARMADO DE CARPETA)",
};

// Estilos mejorados con mejor organización
const styles = StyleSheet.create({
  // Layout básico
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
  },
  content: {
    padding: "50px 65px 50px 60px",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    gap: 0,
  },
  col: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },

  // Tipografía
  bold: {
    fontFamily: "Helvetica-Bold",
  },

  // Bordes
  border: { borderWidth: 1 },
  bTop: { borderTopWidth: 1 },
  bBottom: { borderBottomWidth: 1 },
  bLeft: { borderLeftWidth: 1 },
  bRight: { borderRightWidth: 1 },

  // Espaciado
  cell: {
    paddingHorizontal: 3,
    paddingVertical: 2,
  },

  // Anchos
  w10: { width: "10%" },
  w20: { width: "20%" },
  w40: { width: "40%" },
  w50: { width: "50%" },
  w80: { width: "80%" },
  wfull: { width: "100%" },

  // Elementos específicos
  header: {
    position: "absolute",
    top: -1.2,
    right: "20px",
    zIndex: -10,
  },
  styleFormat: {
    position: "absolute",
    top: "0",
    left: "0",
    zIndex: -10,
    width: "612px",
    height: "792px",
  },
});

// Hook personalizado para cálculos de tributos
const useTributosCalculation = (data) => {
  const extractNumber = (str) => {
    if (!str) return 0;
    const parts = str.split(" ");
    return Number(parts[parts.length - 1] || "0");
  };

  const ga = extractNumber(data?.g1TributosDeterminadosGa);
  const iva = extractNumber(data?.g2TributosDeterminadosIva);
  const ice = extractNumber(data?.g4TributosDeterminadosIce);

  const getTributosAduaneros = (customList) => {
    const defaultWithValues = DEFAULT_TRIBUTOS.map((tributo) => ({
      ...tributo,
      value:
        tributo.key === "ga"
          ? ga
          : tributo.key === "iva"
          ? iva
          : tributo.key === "ice"
          ? ice
          : tributo.value || 0,
    }));

    if (!customList || !validateArray(customList)) {
      return defaultWithValues;
    }

    const existingItems = customList.map((item) => ({
      name: item.label,
      value: Number(item.description || 0),
    }));

    const existingNames = new Set(existingItems.map((item) => item.name));
    const combinedItems = [...existingItems];

    defaultWithValues.forEach((tributo) => {
      if (!existingNames.has(tributo.name)) {
        combinedItems.push({
          name: tributo.name,
          value: tributo.value,
        });
      }
    });

    return combinedItems.reverse();
  };

  return { getTributosAduaneros };
};

// Utilidades para extraer datos
const extractDocumentData = (documents, label) => {
  if (!validateArray(documents)) return "";

  const document = documents.find(
    (item) => item.label?.toUpperCase() === label.toUpperCase()
  );

  return document?.description2?.toString().split(" ")[0] || "";
};

const parseNumericValue = (value, defaultValue = 0) => {
  if (!value) return defaultValue;

  const cleanValue = value.toString().replace(",", ".");
  const numericValue = Number(cleanValue);

  return isNaN(numericValue) ? defaultValue : numericValue;
};

// Componente para renderizar acciones
// Componente para renderizar acciones - VERSIÓN CORREGIDA
const ActionSection = ({
  titulo,
  fecha,
  nombre,
  mensaje,
  instrucciones,
  variant = "default",
}) => {
  const isNormalVariant = variant === "normal";

  return (
    <>
      <View style={[styles.bBottom, styles.bold, { textAlign: "center" }]}>
        <Text style={styles.cell}>{titulo}</Text>
      </View>

      <View style={isNormalVariant ? {} : styles.bBottom}>
        <Text style={styles.cell}>Fecha: {fecha}</Text>
      </View>

      <View
        style={[
          isNormalVariant ? {} : [styles.row, styles.bBottom],
          {
            alignItems: "center",
            minHeight: isNormalVariant ? 20 : "1.5cm", // Cambiar height por minHeight
          },
        ]}
      >
        <Text style={styles.cell}>Nombre: {nombre}</Text>
      </View>

      <View style={[styles.col, isNormalVariant ? {} : styles.bBottom]}>
        {!mensaje ? (
          <View
            style={[
              styles.col,
              styles.cell,
              {
                gap: 1,
                minHeight: "2cm", // Cambiar height por minHeight para que se expanda
                paddingBottom: 8, // Agregar padding inferior
              },
            ]}
          >
            <Text style={{ paddingBottom: 2 }}>
              {isNormalVariant ? "Instrucciones:" : "INSTRUCCIONES:"}
            </Text>
            {Array.isArray(instrucciones) ? (
              instrucciones.map((instruccion, index) => (
                <Text
                  key={index}
                  style={{
                    paddingLeft: 6,
                    textAlign: "justify", // Justificar el texto
                  }}
                >
                  {instruccion}
                </Text>
              ))
            ) : (
              <Text
                style={{
                  paddingLeft: 6,
                  lineHeight: 1.2,
                  textAlign: "justify",
                }}
              >
                {instrucciones}
              </Text>
            )}
          </View>
        ) : (
          <>
            <View
              style={[
                styles.col,
                styles.cell,
                {
                  gap: 1,
                  minHeight: "1cm", // Cambiar height por minHeight
                  paddingBottom: 4,
                },
              ]}
            >
              <Text style={{ paddingBottom: 2 }}>INSTRUCCIONES:</Text>
              {Array.isArray(instrucciones) ? (
                instrucciones.map((instruccion, index) => (
                  <Text
                    key={index}
                    style={{
                      paddingLeft: 6,
                      lineHeight: 1.2,
                      textAlign: "justify",
                    }}
                  >
                    {instruccion}
                  </Text>
                ))
              ) : (
                <Text
                  style={{
                    paddingLeft: 6,
                    lineHeight: 1.2,
                    textAlign: "justify",
                  }}
                >
                  {instrucciones}
                </Text>
              )}
            </View>
            <Text
              style={[
                styles.cell,
                {
                  minHeight: "1cm", // Cambiar height por minHeight
                  paddingBottom: 4,
                },
              ]}
            >
              MENSAJERIA: {mensaje}
            </Text>
          </>
        )}
      </View>
    </>
  );
};

// Componente para renderizar pagos
const PaymentSection = ({ title, items, showTotal = false }) => {
  const totalValue = showTotal
    ? items.reduce((sum, item) => {
        const itemValue = parseNumericValue(item.valor);
        return sum + itemValue;
      }, 0)
    : null;

  return (
    <View>
      <View style={[styles.bBottom, styles.bold, { textAlign: "center" }]}>
        <Text style={styles.cell}>{title}</Text>
      </View>

      {items.map((item, index) => (
        <View
          key={index}
          style={[styles.row, { gap: 2, fontSize: 8, alignItems: "center" }]}
        >
          <View style={styles.w80}>
            <Text style={{ paddingHorizontal: 3, paddingVertical: 1 }}>
              {item.nombre}
            </Text>
          </View>
          <View style={[styles.w20, { textAlign: "right" }]}>
            <Text style={{ paddingHorizontal: 3, paddingVertical: 1 }}>
              {numberWithDecimals(item.valor)}
            </Text>
          </View>
        </View>
      ))}

      {showTotal && (
        <View
          style={[
            styles.row,
            styles.bTop,
            styles.bold,
            { gap: 2, fontSize: 8, height: "30px" },
          ]}
        >
          <View style={[styles.w80, styles.bRight]}>
            <Text style={{ paddingHorizontal: 3, paddingVertical: 1 }}>
              TOTAL
            </Text>
          </View>
          <View style={[styles.w20, { textAlign: "right" }]}>
            <Text style={{ paddingHorizontal: 3, paddingVertical: 1 }}>
              {numberWithDecimals(totalValue)}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

// Componente principal del documento PDF
const PDFDocument = ({ data, dataHoja }) => {
  const procedureHistories = validateArray(dataHoja.procedureHistories)
    ? dataHoja.procedureHistories
    : [];

  // Cálculos financieros
  const tc = parseNumericValue(data.e16TipoDeCambio);
  const f6 = parseNumericValue(data.f6TotalValorFob);
  const f7 = parseNumericValue(data.f7GastosTransHastaElLugarDeImportación);
  const f8 = parseNumericValue(data.f8TotalCostoDelSeguro);
  const f9 = parseNumericValue(
    data["f9GastosDeCarga,Descarga,ManipuleoYOtrosGastos"]
  );
  const f10 = parseNumericValue(data.f10TotalValorCifAduana) * tc;

  // Extracción de tributos
  const extractTribute = (tributeData) => {
    if (!tributeData) return 0;
    const parts = tributeData.split(" ");
    return parseNumericValue(parts[parts.length - 1]);
  };

  const ga = extractTribute(data.g1TributosDeterminadosGa);
  const iva = extractTribute(data.g2TributosDeterminadosIva);
  const ice = extractTribute(data.g4TributosDeterminadosIce);

  // Extracción de documentos
  const factura = extractDocumentData(
    data.documentosSeccionLSubDatas,
    "FACTURA COMERCIAL"
  );
  const dav = extractDocumentData(
    data.documentosSeccionLSubDatas,
    "DECLARACION ANDINA DE VALOR (DAV)"
  );

  const { getTributosAduaneros } = useTributosCalculation(data);

  // Función helper para obtener fecha de procedimiento
  const getProcedureDate = (step) => {
    const procedure = procedureHistories.find((item) => item.step === step);
    return procedure?.initialDate
      ? dayjs(procedure.initialDate).format("DD/MM/YYYY")
      : "";
  };

  // Cálculo de gastos
  const calculateSubDatasTotal = (subDatas) => {
    if (!validateArray(subDatas)) return 0;
    return subDatas.reduce(
      (sum, item) => sum + parseNumericValue(item.description),
      0
    );
  };

  const gastosDespacho =
    calculateSubDatasTotal(data.gastosFacturadosSubDatas) +
    calculateSubDatasTotal(data.gastosNoFacturadosSubDatas);

  const honorarios = calculateSubDatasTotal(
    data.honorariosProfesionalesSubDatas
  );

  const tributosTotal =
    getTributosAduaneros(data?.tributosAduanerosSubDatas)?.reduce(
      (acc, item) => acc + parseNumericValue(item.value),
      0
    ) || 0;

  return (
    <Document>
      {/* Primera página */}
      <Page size="LETTER" style={styles.page}>
        {/* Formato de fondo */}
        <View style={styles.styleFormat} fixed>
          <Image
            src="https://res.cloudinary.com/joeldes/image/upload/v1751556894/DeliveryDay/reports/DD_formato-informe_xdmzwo.jpg"
            style={{ width: "100%", height: "100%" }}
          />
        </View>
        <View style={styles.content}>
          {/* Título */}
          <View
            style={[
              styles.row,
              styles.bold,
              styles.wfull,
              { paddingBottom: 10 },
            ]}
          >
            <View style={styles.w50} />
            <View
              style={[
                styles.row,
                styles.w50,
                { fontSize: 12, justifyContent: "flex-end" },
              ]}
            >
              <Text style={{ paddingHorizontal: 5, paddingVertical: 3 }}>
                N° DE REF. DE LA DIM
              </Text>
              <Text
                style={[styles.border, { paddingHorizontal: 5, paddingTop: 2 }]}
              >
                {data.a3NúmeroDeReferencia || "       "}
              </Text>
            </View>
          </View>

          {/* Contenido principal */}
          <View style={{ fontSize: 8 }}>
            {/* Encabezado de aduana */}
            <View
              style={[
                styles.bTop,
                styles.bLeft,
                styles.bRight,
                styles.w40,
                styles.cell,
              ]}
            >
              <Text>ADM DE ADUANA: {data.a4AduanaDeDespacho}</Text>
            </View>

            <View style={styles.border}>
              {/* Información general */}
              <View style={styles.col}>
                <View style={styles.row}>
                  <View
                    style={[
                      styles.col,
                      styles.w40,
                      styles.bRight,
                      styles.bBottom,
                    ]}
                  >
                    <Text style={[styles.bBottom, styles.cell]}>
                      MODALIDAD DE DESPACHO: {data.modalidadDeDespacho}
                    </Text>
                    <Text style={styles.cell}>
                      Nro. DE FACTURA COMERCIAL: {factura}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.col,
                      styles.w20,
                      styles.bRight,
                      styles.bBottom,
                    ]}
                  >
                    <Text style={styles.cell}>ITEMS:</Text>
                    <Text style={styles.cell}>{data.f2TotalNúmeroDeItems}</Text>
                  </View>
                  <View style={[styles.col, styles.w40, styles.bBottom]}>
                    <Text style={styles.cell}>
                      IMPORTADOR: {data.company?.razonSocial}
                    </Text>
                    <Text style={styles.cell}>NIT: {data.company?.nit}</Text>
                  </View>
                </View>

                <View
                  style={[
                    styles.row,
                    styles.bBottom,
                    { justifyContent: "space-between" },
                  ]}
                >
                  <View style={[styles.col, styles.w40]}>
                    <Text style={[styles.bRight, styles.cell]}>
                      PROVEEDOR:{" "}
                      {data.e1DatosDelProveedor?.toString().split(",")[0]}
                    </Text>
                  </View>
                  <View style={styles.cell}>
                    <Text>Nº DAM: {data["a1N°DeDeclaración(Dam)"]}</Text>
                  </View>
                  <View style={styles.cell}>
                    <Text>Nº DAV: {dav || data.internoDeDav}</Text>
                  </View>
                  <View style={styles.cell}>
                    <Text>Nº DIM: {data.a1NúmeroDeDeclaración}</Text>
                  </View>
                </View>

                <View style={[styles.row, styles.bBottom]}>
                  <View style={[styles.col, styles.wfull]}>
                    <Text style={[styles.bRight, styles.cell]}>
                      DATOS DE RECEPCION:{" "}
                      {data?.procFechaDeInicio
                        ? dayjs(data.procFechaDeInicio).format("DD/MM/YYYY")
                        : ""}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Secciones de acciones */}
              <View style={styles.row}>
                <View style={[styles.w50, styles.bRight]}>
                  <ActionSection
                    titulo={STEP_MAPPINGS[1]}
                    fecha={getProcedureDate(1)}
                    nombre="Maribel / Belzabe"
                    instrucciones={[
                      data["a1N°DeDeclaración(Dam)"],
                      data["instruciones(RecepcionDeDocumentos)"],
                    ]}
                  />
                </View>
                <View style={styles.w50}>
                  <ActionSection
                    titulo={STEP_MAPPINGS[6]}
                    fecha={getProcedureDate(6)}
                    nombre={data.designacionDeLiquidador}
                    instrucciones={[
                      data.a1NúmeroDeDeclaración,
                      data["instruciones(LiquidacionesArmadoDeCarpeta)"],
                    ]}
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={[styles.w50, styles.bRight]}>
                  <ActionSection
                    titulo={STEP_MAPPINGS[10]}
                    fecha={getProcedureDate(10)}
                    nombre={data.asignarGestor}
                    instrucciones={[
                      data["canal(Rojo,AmarilloOVerde)"],
                      data.internoDeDav,
                      data["instruciones(CanalYGestoria)"],
                    ]}
                  />
                </View>
                <View style={styles.w50}>
                  <ActionSection
                    titulo={STEP_MAPPINGS[12]}
                    fecha={getProcedureDate(12)}
                    nombre="Maribel"
                    mensaje=""
                    instrucciones={[data["instruciones(Adm_Financiera)"]]}
                  />
                </View>
              </View>

              <View style={[styles.row, styles.bBottom]}>
                <View style={[styles.w50, styles.bRight]}>
                  <ActionSection
                    titulo={STEP_MAPPINGS[14]}
                    fecha={getProcedureDate(14)}
                    nombre="Maribel / Nely"
                    variant="normal"
                    instrucciones={[data["instruciones(Archivos)"]]}
                  />
                </View>
                <View style={styles.w50}>
                  <PaymentSection
                    title="OTROS PAGOS DURANTE EL DESPACHO"
                    items={[
                      { nombre: "Tributos Aduaneros", valor: tributosTotal },
                      {
                        nombre: "Gastos de Despacho Aduanero",
                        valor: gastosDespacho,
                      },
                      { nombre: "Honorarios profesionales", valor: honorarios },
                    ]}
                  />
                </View>
              </View>

              <View style={[styles.row, styles.bBottom]}>
                <View style={[styles.w50, styles.bRight]}>
                  <PaymentSection
                    title="LIQUIDACION MANUAL"
                    items={[
                      { nombre: "Total valor FOB (USD)", valor: f6 },
                      {
                        nombre:
                          "Gastos Trans hasta el lugar de importación (USD)",
                        valor: f7,
                      },
                      { nombre: "Total costo del seguro (USD)", valor: f8 },
                      {
                        nombre:
                          "Gastos de carga, descarga, manipuleo y otros gastos (USD)",
                        valor: f9,
                      },
                      { nombre: "Total valor CIF aduana (USD)", valor: f10 },
                      {
                        nombre: "Total Dolares",
                        valor: f6 + f7 + f8 + f9 + f10,
                      },
                      {
                        nombre: "Total Bolivianos",
                        valor: tc ? (f6 + f7 + f8 + f9 + f10) / tc : 0,
                      },
                      { nombre: "GA", valor: ga },
                      { nombre: "IVA", valor: iva },
                      { nombre: "ICE", valor: ice },
                      { nombre: "SISTEMA SUMA", valor: 100 },
                    ]}
                  />
                </View>

                <View style={styles.w50}>
                  <View style={{ textAlign: "center" }}>
                    <Text style={[styles.bold, styles.bBottom, styles.cell]}>
                      DOCUMENTOS DECLARADOS
                    </Text>
                    <View
                      style={[
                        styles.row,
                        {
                          fontSize: 8,
                          gap: 7,
                          paddingVertical: 1,
                          paddingHorizontal: 3,
                        },
                      ]}
                    >
                      <View
                        style={[styles.wfull, { gap: 2, paddingHorizontal: 1 }]}
                      >
                        <CustomListPDF
                          textAlign="normal"
                          items={
                            validateArray(data?.documentosSeccionLSubDatas)
                              ? data.documentosSeccionLSubDatas.map((item) => ({
                                  text: `${item.label} (${item.description5})`,
                                }))
                              : []
                          }
                        />
                      </View>
                    </View>
                    <View style={[styles.bTop, { fontSize: 7 }]}>
                      <Text style={[styles.cell, styles.bold]}>
                        OTRAS INSTRUCCIONES Y OBSERVACIONES GERENCIA
                      </Text>
                      <View
                        style={[
                          styles.bTop,
                          { fontSize: 7, paddingHorizontal: 3 },
                        ]}
                      >
                        <Text>
                          (LA FISCALIZACIÓN ESTA SUJETO A 8 AÑOS A CANALES
                          VERDES Y LA LIQUIDACIÓN NO ES RESPONSABILIDAD DE LA
                          AGENCIA. ABALADO POR EL ART. 283 Y 57 LGA)
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
      {/* Segunda página - Trazabilidad */}
      <Page size="LETTER" style={styles.page}>
        <View style={styles.styleFormat} fixed>
          <Image
            src="https://res.cloudinary.com/joeldes/image/upload/v1751556894/DeliveryDay/reports/DD_formato-informe_xdmzwo.jpg"
            style={{ width: "100%", height: "100%" }}
          />
        </View>
        <View style={styles.content}>
          <View style={{ fontSize: 8 }}>
            <Text
              style={{
                marginTop: "5px",
                fontSize: "10px",
                textAlign: "center",
                fontWeight: "bold",
              }}
            >
              TRAZABILIDAD POR PROCESOS
            </Text>
            <Text
              style={[
                styles.bold,
                { borderRight: true, borderBottom: true, borderLeft: true },
              ]}
            >
              Duración Total: {dataHoja.totalDuration}
            </Text>
            <CustomTablePDF
              fields={[
                { label: "N°", name: "step", width: "4" },
                { label: "Encargado", name: "userInChange", width: "17" },
                { label: "Paso", name: "stepName", width: "18" },
                {
                  label: "Inicio",
                  name: "initialDate",
                  type: "date",
                  width: "17",
                },
                {
                  label: "Final",
                  name: "finalDate",
                  type: "date",
                  width: "17",
                },
                { label: "Duración", name: "durationString", width: "28" },
              ]}
              data={dataHoja.procedureHistories}
              border={{ top: true, right: true, bottom: true, left: true }}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Componente principal
export const GenerateRoadmapPDF = ({ procedure }) => {
  const buildFieldsQuery = () =>
    FIELD_NAMES.map((field) => `fieldnames=${field}`).join("&");

  const { data, loading } = Request({
    urlApi: `/Procedure/${
      procedure.id || procedure.procedureId
    }/dataValues?${buildFieldsQuery()}`,
  });

  const { data: dataHoja, loading: loadingHoja } = Request({
    urlApi: `/tradecruz/${procedure.id || procedure.procedureId}/HojaDeRuta`,
  });

  // Mostrar loading mientras se cargan los datos
  if (!loading || !loadingHoja) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <p>Cargando documento...</p>
      </div>
    );
  }

  // Validar que tenemos los datos necesarios
  if (!data || !dataHoja) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <p>Error: No se pudieron cargar los datos del procedimiento.</p>
      </div>
    );
  }

  return (
    <div>
      <PDFViewer
        style={{
          width: 900,
          height: window.innerHeight - 100,
        }}
      >
        <PDFDocument data={data} dataHoja={dataHoja} />
      </PDFViewer>
    </div>
  );
};
